import type { ReactNode } from 'react'
import { create } from 'zustand'
import {
  type OpenSidePanelOptions,
  SIDE_PANEL_STORAGE_KEY,
  SIDE_PANEL_WIDTH,
  type SidePanelNavigationEntry,
  type SidePanelPage,
  type SidePanelParams
} from '@/shared/components/side-panel/side-panel-types'

const createPageId = () => crypto.randomUUID()

const getInitialWidth = () => {
  if (typeof window === 'undefined') return SIDE_PANEL_WIDTH.default

  const storedWidth = Number(window.localStorage.getItem(SIDE_PANEL_STORAGE_KEY))
  if (!Number.isFinite(storedWidth)) return SIDE_PANEL_WIDTH.default

  return Math.min(SIDE_PANEL_WIDTH.max, Math.max(SIDE_PANEL_WIDTH.min, storedWidth))
}

type SidePanelState = {
  isOpen: boolean
  isClosing: boolean
  /** Una capa modal está consumiendo Escape y el panel no debe competir por esa tecla. */
  isEscapeCloseSuppressed: boolean
  currentPage: SidePanelPage | null
  params: SidePanelParams | null
  navigationStack: SidePanelNavigationEntry[]
  width: number
  title: string
  content: ReactNode | null
  icon: ReactNode | null
  /** Footer fijo (no scrollea) — típicamente <SidePanelActions/>. Null = sin footer. */
  footer: ReactNode | null
  openSidePanel: <TPage extends SidePanelPage>(opts: OpenSidePanelOptions<TPage>) => void
  goBack: () => void
  setWidth: (width: number) => void
  cleanupClosedPanel: () => void
  setEscapeCloseSuppressed: (suppressed: boolean) => void
  openPanel: (opts: {
    content: ReactNode
    title?: string
    icon?: ReactNode
    footer?: ReactNode
  }) => void
  closePanel: () => void
}

export const useSidePanelStore = create<SidePanelState>()((set) => ({
  isOpen: false,
  isClosing: false,
  isEscapeCloseSuppressed: false,
  currentPage: null,
  params: null,
  navigationStack: [],
  width: getInitialWidth(),
  content: null,
  title: '',
  icon: null,
  footer: null,
  openSidePanel: ({ page, params, resetStack = true }) => {
    set((state) => {
      const entry: SidePanelNavigationEntry = { page, params, pageId: createPageId() }

      return {
        isOpen: true,
        isClosing: false,
        currentPage: page,
        params,
        navigationStack: resetStack ? [entry] : [...state.navigationStack, entry],
        content: null,
        title: '',
        icon: null,
        footer: null
      }
    })
  },
  goBack: () => {
    set((state) => {
      const nextStack = state.navigationStack.slice(0, -1)
      const previous = nextStack.at(-1)

      if (!previous) {
        return {
          isOpen: false,
          isClosing: state.isOpen,
          navigationStack: []
        }
      }

      return {
        currentPage: previous.page,
        params: previous.params,
        navigationStack: nextStack
      }
    })
  },
  setWidth: (width) => {
    const nextWidth = Math.min(SIDE_PANEL_WIDTH.max, Math.max(SIDE_PANEL_WIDTH.min, width))
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIDE_PANEL_STORAGE_KEY, String(nextWidth))
    }
    set({ width: nextWidth })
  },
  cleanupClosedPanel: () => {
    set((state) => {
      if (state.isOpen) return state

      return {
        isClosing: false,
        currentPage: null,
        params: null,
        navigationStack: [],
        content: null,
        title: '',
        icon: null,
        footer: null
      }
    })
  },
  setEscapeCloseSuppressed: (isEscapeCloseSuppressed) => {
    set({ isEscapeCloseSuppressed })
  },
  openPanel: ({ content, title = '', icon, footer = null }) => {
    set({
      isOpen: true,
      isClosing: false,
      currentPage: null,
      params: null,
      navigationStack: [],
      content,
      title,
      icon,
      footer
    })
  },
  closePanel: () => {
    set((state) => ({
      isOpen: false,
      isClosing: state.isOpen
    }))
  }
}))
